// Paketler — /clients/kpi/paketler (spec §5 "Paketler", DR4/DR6/DR15). Sektöre göre hazır gösterge
// kümeleri: sol 280 px rail (sektör gruplu liste + arama + "+ Yeni paket"; ≤ 1024 px yerel
// `<select>`), sağda PackageEditor (düz bölümler). `?packageId=` derin bağlantısı (Set kur / Sete
// ekle "Paketi düzenle ↗") açılışta o paketi seçer. Taslak varken başka pakete geçiş onay ister.
// Menü `kpi.manage`; okuma `kpi.view` — yetkisiz kullanıcıda düzenleme eylemleri görünmez.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import type { KpiFrameworkOption, KpiMetric, KpiPackage } from "@mahir/shared";
import { listKpiFrameworks, listKpiMetrics } from "../../api/kpi";
import { ConfirmDialog } from "../../components/common/ConfirmDialog";
import { ErrorState } from "../../components/common/StateSurface";
import { KpiScreen } from "../../components/kpi/KpiScreen";
import { PackageEditor } from "../../components/kpi/PackageEditor";
import { PackageEmptyStates, packageEmptyState } from "../../components/kpi/PackageEmptyStates";
import { PackageIdentityModal } from "../../components/kpi/PackageIdentityModal";
import { PackageRail, groupPackages } from "../../components/kpi/PackageRail";
import { helpText } from "../../components/kpi/kpi-form-styles";
import { useMediaQuery } from "../../hooks/use-media-query";
import { draftContext, type DraftContext } from "../../lib/kpi-package-draft";
import { KPI_PACKAGES_COPY } from "../../lib/kpi-packages-copy";
import { useAuthStore } from "../../stores/auth-store";
import { useKpiPackagesStore } from "../../stores/kpi-packages-store";

const C = KPI_PACKAGES_COPY;

/** Açılış seçimi: derin bağlantıdaki paket (listede varsa), yoksa ilk gruptaki ilk etkin paket. */
export function initialPackageId(
  packages: readonly KpiPackage[],
  requested: string | null,
): string | null {
  if (requested && packages.some((p) => p.id === requested)) return requested;
  const { groups, archived } = groupPackages(packages, "");
  return groups[0]?.packages[0]?.id ?? archived[0]?.id ?? null;
}

/**
 * Liste her geldiğinde seçim kararı. Önbellekteki (bayat) liste derin bağlantıdaki yeni paketi henüz
 * içermeyebilir (ör. "Bu setten paket oluştur" → "Paketi aç"): o zaman yedek seçim yapılır ve
 * `auto` işaretlenir; taze liste istenen paketi getirince yedek seçim istenene döner. Kullanıcının
 * kendi seçimi (`auto: false`) ezilmez. e2e 15 "1b" yakaladı.
 */
export function nextPackageSelection(
  cur: { id: string | null; auto: boolean },
  packages: readonly KpiPackage[],
  requested: string | null,
): { id: string | null; auto: boolean } {
  const has = (id: string | null): boolean => id !== null && packages.some((p) => p.id === id);
  if (has(cur.id) && !(cur.auto && requested !== cur.id && has(requested))) return cur;
  const id = initialPackageId(packages, requested);
  return { id, auto: !(requested !== null && id === requested) };
}

/** Boş durum seçimi için katalog sayıları: ortak kategori ve kategori olmayan ortak gösterge. */
export function catalogCounts(ctx: DraftContext): { categories: number; items: number } {
  let items = 0;
  for (const m of ctx.byId.values()) {
    if (m.clientId === null && !ctx.categories.has(m.id) && m.status === "active") items += 1;
  }
  return { categories: ctx.categories.size, items };
}

export type PaketlerBodyState = "packagesError" | "catalogError" | "skeleton" | "empty" | "layout";

/**
 * Gövde kararı: liste hatası → yeniden dene; katalog yüklenemedi → kendi hatası (boş durum dalı
 * kataloğa bakar, "kategori yok" sanılmaz); liste ya da katalog yüklenmeden iskelet.
 */
export function paketlerBodyState(a: {
  loaded: boolean;
  packagesFailed: boolean;
  metricsLoaded: boolean;
  metricsFailed: boolean;
  emptyKind: string | null;
}): PaketlerBodyState {
  if (!a.loaded && a.packagesFailed) return "packagesError";
  if (a.metricsFailed) return "catalogError";
  if (!a.loaded || !a.metricsLoaded) return "skeleton";
  if (a.emptyKind && a.emptyKind !== "noMatch") return "empty";
  return "layout";
}

/** DR6 yükleme: sol liste + panel iskeleti (3 satır). */
function PackagesSkeleton(): JSX.Element {
  const bar = (w: number | string): JSX.Element => (
    <div
      aria-hidden="true"
      style={{ height: 40, width: w, borderRadius: 8, background: "var(--color-bg)" }}
    />
  );
  return (
    <div role="status" aria-label={C.empty.loadingPackages} className="flex items-start gap-6">
      <div className="grid gap-2" style={{ width: 280, flexShrink: 0 }}>
        {bar("100%")}
        {bar("100%")}
        {bar("100%")}
      </div>
      <div className="grid min-w-0 flex-1 gap-2">
        {bar("60%")}
        {bar("100%")}
        {bar("100%")}
      </div>
    </div>
  );
}

export function KpiPaketlerPage(): JSX.Element {
  const user = useAuthStore((s) => s.user);
  const canManage = user?.permissions["kpi.manage"] === true;
  const { packages, packagesLoadedAt, packagesError, loadPackages, invalidatePackages } =
    useKpiPackagesStore();
  const [params, setParams] = useSearchParams();
  const requested = params.get("packageId");
  const narrow = useMediaQuery("(max-width: 1024px)");
  const [metrics, setMetrics] = useState<KpiMetric[] | null>(null);
  const [metricsFailed, setMetricsFailed] = useState(false);
  const [frameworks, setFrameworks] = useState<KpiFrameworkOption[]>([]);
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  /** seçim yedek mi (derin bağlantıdaki paket listede yokken) — kullanıcı seçince false */
  const autoPicked = useRef(false);
  const [dirty, setDirty] = useState(false);
  const [pendingSwitch, setPendingSwitch] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  const loadMetrics = useCallback(() => {
    setMetricsFailed(false);
    listKpiMetrics({ includeDeprecated: true })
      .then((r) => setMetrics(r.metrics))
      .catch(() => setMetricsFailed(true));
  }, []);

  useEffect(() => {
    void loadPackages(true);
    loadMetrics();
    listKpiFrameworks()
      .then(setFrameworks)
      .catch(() => setFrameworks([]));
  }, [loadPackages, loadMetrics]);

  const loaded = packagesLoadedAt !== null || packages.length > 0;
  // Karar güncelleyici (updater) DIŞINDA: StrictMode updater'ı iki kez çağırır, ref yan etkisi
  // ikinci çağrıda kararı geri alırdı.
  useEffect(() => {
    if (!loaded) return;
    const next = nextPackageSelection(
      { id: selectedId, auto: autoPicked.current },
      packages,
      requested,
    );
    autoPicked.current = next.auto;
    if (next.id !== selectedId) setSelectedId(next.id);
  }, [loaded, packages, requested, selectedId]);

  const ctx = useMemo(() => draftContext(metrics ?? []), [metrics]);
  const { visibleCount } = groupPackages(packages, search);
  const emptyKind = packageEmptyState({
    packageCount: packages.length,
    visibleCount,
    catalog: metrics ? catalogCounts(ctx) : null,
    selectedMembers: null,
  });

  const select = useCallback(
    (id: string): void => {
      if (id === selectedId) return;
      if (dirty) {
        setPendingSwitch(id);
        return;
      }
      autoPicked.current = false;
      setSelectedId(id);
      setParams({ packageId: id }, { replace: true });
    },
    [dirty, selectedId, setParams],
  );

  const refresh = useCallback(() => {
    invalidatePackages();
    void loadPackages(true);
  }, [invalidatePackages, loadPackages]);

  const frameworkName = useCallback(
    (id: string) => frameworks.find((f) => f.id === id)?.name ?? null,
    [frameworks],
  );

  const state = paketlerBodyState({
    loaded,
    packagesFailed: packagesError !== null,
    metricsLoaded: metrics !== null,
    metricsFailed,
    emptyKind,
  });
  let body: JSX.Element;
  if (state === "packagesError") {
    body = <ErrorState message={C.page.loadFailed} onRetry={() => void loadPackages(true)} />;
  } else if (state === "catalogError") {
    body = (
      <ErrorState
        message={C.page.catalogLoadFailed}
        onRetry={loadMetrics}
        retryLabel={C.page.retry}
      />
    );
  } else if (state === "skeleton") {
    body = <PackagesSkeleton />;
  } else if (state === "empty" && emptyKind) {
    body = (
      <PackageEmptyStates
        kind={emptyKind}
        canManage={canManage}
        onCreate={() => setCreateOpen(true)}
      />
    );
  } else {
    body = (
      <div
        className={narrow ? "grid gap-4" : "flex items-stretch gap-6"}
        data-testid="kpi-packages-layout"
        style={narrow ? undefined : { flex: 1, minHeight: 0 }}
      >
        <div style={narrow ? {} : { width: 280, flexShrink: 0, minHeight: 0, overflowY: "auto" }}>
          <PackageRail
            packages={packages}
            selectedId={selectedId}
            search={search}
            onSearch={setSearch}
            onSelect={select}
            onCreate={canManage ? () => setCreateOpen(true) : null}
            narrow={narrow}
          />
        </div>
        <div
          className="min-w-0 flex-1"
          style={narrow ? undefined : { display: "flex", flexDirection: "column", minHeight: 0 }}
        >
          {selectedId ? (
            <PackageEditor
              key={selectedId}
              packageId={selectedId}
              metrics={metrics}
              ctx={ctx}
              canManage={canManage}
              frameworkName={frameworkName}
              onDirtyChange={setDirty}
              onChanged={refresh}
              fill={!narrow}
              narrow={narrow}
            />
          ) : (
            <p style={helpText}>{C.page.noSelection}</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <KpiScreen
      title={C.page.title}
      subtitle={C.page.description}
      hideClient
      hideSet
      requireSet={false}
      {...(narrow ? { scrollBody: true } : { fullHeight: true })}
    >
      {body}
      <PackageIdentityModal
        open={createOpen}
        pkg={null}
        onClose={() => setCreateOpen(false)}
        onSaved={(pkg) => {
          setCreateOpen(false);
          invalidatePackages();
          // yeni paket listeye girdikten sonra seçilir (taslak varsa geçiş onayı yine sorulur)
          void loadPackages(true).then(() => select(pkg.id));
        }}
      />
      <ConfirmDialog
        open={pendingSwitch !== null}
        title={C.page.confirmSwitchTitle}
        body={C.page.confirmSwitch}
        confirmLabel={C.page.confirmSwitchAction}
        cancelLabel={C.page.cancel}
        tone="danger"
        onConfirm={() => {
          const id = pendingSwitch;
          setPendingSwitch(null);
          setDirty(false);
          if (id) {
            autoPicked.current = false;
            setSelectedId(id);
            setParams({ packageId: id }, { replace: true });
          }
        }}
        onCancel={() => setPendingSwitch(null)}
      />
    </KpiScreen>
  );
}
